import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, X, Check, Pencil, Ban, HelpCircle, Info } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTENTS = [
  ['GENERAL_SITE_REVIEW', 'General Site Review'], ['PROGRESS_EVIDENCE', 'Progress Evidence'],
  ['QUALITY_REVIEW', 'Quality Review'], ['SAFETY_REVIEW', 'Safety Review'],
  ['HOUSEKEEPING', 'Housekeeping'], ['ACCESS_OBSTRUCTION', 'Access / Obstruction'],
  ['FINISHING_WORKMANSHIP', 'Finishing / Workmanship'],
];
const CONFIRM_MAP = { QUALITY_REVIEW: 'QUALITY', FINISHING_WORKMANSHIP: 'QUALITY', SAFETY_REVIEW: 'SAFETY', HOUSEKEEPING: 'SAFETY', ACCESS_OBSTRUCTION: 'SAFETY', PROGRESS_EVIDENCE: 'PROGRESS' };

/** Governed 360 AI Assist: proposal -> human review -> confirmed existing record. AI is assistive only. */
export default function SiteWalkAiAssist({ projectId, capturePoint, onConfirmed, onClose }) {
  const [intent, setIntent] = useState('GENERAL_SITE_REVIEW');
  const [scope, setScope] = useState('BROADER');
  const [prop, setProp] = useState(null);
  const [busy, setBusy] = useState(false);
  const [desc, setDesc] = useState('');

  const analyze = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/projects/${projectId}/sitewalk-ai/analyze`,
        { panoramaId: capturePoint.panoramaId, analysisIntent: intent, scope });
      setProp(r.data); setDesc('');
      if (r.data.reviewStatus === 'AI_UNAVAILABLE') toast.info('AI-assisted analysis is not configured — manual review remains available.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Analysis not permitted'); }
    setBusy(false);
  };

  const review = async (action) => {
    try {
      const r = await axios.post(`${API}/projects/${projectId}/sitewalk-ai/proposals/${prop.id}/review`,
        { action, description: desc, reviewedClassification: desc });
      setProp(r.data); toast.success(`Marked ${action}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const confirm = async () => {
    const recordType = CONFIRM_MAP[intent] || 'ISSUE';
    try {
      const r = await axios.post(`${API}/projects/${projectId}/sitewalk-ai/proposals/${prop.id}/confirm`,
        { recordType, description: desc || prop.reviewedDescription, title: desc || 'Site walk finding' });
      toast.success(`Confirmed ${r.recordType || recordType} record`);
      onConfirmed && onConfirmed(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Confirmation failed'); }
  };

  const canReview = prop && ['PENDING_REVIEW', 'AI_UNAVAILABLE', 'INSUFFICIENT_EVIDENCE'].includes(prop.reviewStatus);
  const canConfirm = prop && ['ACCEPTED', 'CORRECTED'].includes(prop.reviewStatus);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-3" data-testid="ai-assist-modal">
      <div className="bg-white rounded-xl w-full max-w-lg p-5 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-slate-800 flex items-center gap-2"><Sparkles className="w-5 h-5 text-violet-600" /> AI Assist <span className="text-[10px] font-normal px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded">assistive · human-reviewed</span></h3>
          <button onClick={onClose} data-testid="ai-assist-close"><X className="w-5 h-5 text-slate-400" /></button>
        </div>

        {!prop && (
          <>
            <label className="text-xs font-medium text-slate-600">Analysis intent</label>
            <select value={intent} onChange={(e) => setIntent(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm mt-1 mb-3" data-testid="ai-intent-select">
              {INTENTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <label className="text-xs font-medium text-slate-600">Scope</label>
            <select value={scope} onChange={(e) => setScope(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm mt-1 mb-3" data-testid="ai-scope-select">
              <option value="BROADER">Broader panorama review (multi-view)</option>
              <option value="CURRENT_VIEW">Current view only</option>
            </select>
            <p className="text-[11px] text-slate-400 mb-3 flex items-start gap-1"><Info className="w-3.5 h-3.5 mt-0.5" /> AI proposes potential visible findings for human review. It never creates NCRs, sets safety risk, or updates progress automatically.</p>
            <button onClick={analyze} disabled={busy} className="w-full py-2 bg-violet-600 text-white rounded-lg text-sm disabled:opacity-50" data-testid="ai-analyze-btn">{busy ? 'Analyzing…' : 'Run AI Analysis'}</button>
          </>
        )}

        {prop && (
          <div data-testid="ai-proposal-panel">
            <div className="text-[11px] text-slate-500 mb-2">Intent: <b>{prop.analysisIntent}</b> · Coverage: {prop.analysisCoverage} · {(prop.frames || []).length} frames</div>
            {prop.reviewStatus === 'AI_UNAVAILABLE' && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-3" data-testid="ai-unavailable-note">
                AI-assisted analysis is not configured. Manual Site Walk review remains available — enter your own finding below to record it.
              </div>
            )}
            {prop.reviewStatus === 'INSUFFICIENT_EVIDENCE' && (
              <div className="text-xs text-slate-600 bg-slate-50 border rounded p-2 mb-3" data-testid="ai-insufficient-note">Insufficient visible evidence to conclude — not "no defect" / not "safe" / not "0%".</div>
            )}
            {Array.isArray(prop.proposals) && prop.proposals.length > 0 && (
              <ul className="text-xs text-slate-700 list-disc pl-4 mb-3" data-testid="ai-proposals-list">
                {prop.proposals.slice(0, 6).map((p, i) => <li key={i}>{typeof p === 'string' ? p : (p.description || p.label || JSON.stringify(p))}</li>)}
              </ul>
            )}
            <span className={`text-[11px] px-2 py-0.5 rounded-full ${prop.reviewStatus === 'REJECTED' ? 'bg-red-100 text-red-700' : canConfirm ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`} data-testid="ai-review-status">{prop.reviewStatus}</span>

            {canReview && (
              <>
                <label className="text-xs font-medium text-slate-600 block mt-3">Human finding / correction</label>
                <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={2} className="w-full border rounded-lg px-3 py-2 text-sm mt-1 mb-2" placeholder="Describe the confirmed finding" data-testid="ai-review-desc" />
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => review('ACCEPTED')} className="text-xs flex items-center gap-1 px-2.5 py-1.5 border rounded-lg" data-testid="ai-accept-btn"><Check className="w-3.5 h-3.5" /> Accept</button>
                  <button onClick={() => review('CORRECTED')} className="text-xs flex items-center gap-1 px-2.5 py-1.5 border rounded-lg" data-testid="ai-correct-btn"><Pencil className="w-3.5 h-3.5" /> Correct</button>
                  <button onClick={() => review('REJECTED')} className="text-xs flex items-center gap-1 px-2.5 py-1.5 border rounded-lg" data-testid="ai-reject-btn"><Ban className="w-3.5 h-3.5" /> Reject</button>
                  <button onClick={() => review('INSUFFICIENT_EVIDENCE')} className="text-xs flex items-center gap-1 px-2.5 py-1.5 border rounded-lg" data-testid="ai-insufficient-btn"><HelpCircle className="w-3.5 h-3.5" /> Insufficient</button>
                </div>
              </>
            )}
            {canConfirm && (
              <button onClick={confirm} className="w-full mt-3 py-2 bg-emerald-600 text-white rounded-lg text-sm" data-testid="ai-confirm-btn">
                Confirm as {CONFIRM_MAP[intent] || 'ISSUE'} record
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
